import { CHART_PALETTE } from '@/lib/chart-theme'
import { StatCard, fmt$, InfoTooltip } from './shared'

interface Stage {
  stage: string
  count: number
  value: number
}

export function PipelineReport({ data }: { data: any }) {
  const { summary, chartData } = data
  const { totalPipelineValue, totalItems, stageCount } = summary
  const { stages } = chartData

  return (
    <div className="rep-report">
      <div className="rep-stats-row">
        <StatCard title="Total Pipeline Value" value={fmt$(totalPipelineValue)} color="#0074F4" />
        <StatCard title="Total Items" value={totalItems.toLocaleString()} color="#8B47FB" />
        <StatCard title="Active Stages" value={stageCount.toString()} color="#7CDBE7" />
      </div>

      <div className="rep-charts-row">
        <div className="rep-chart-card rep-chart-full">
          <InfoTooltip text="A treemap shows pipeline value as proportional blocks, making it easy to see which stages hold the most money at a glance." />
          <TreemapChart stages={stages} total={totalPipelineValue} />
        </div>
      </div>

      <div className="rep-table-card">
        <h3 className="rep-chart-title">Stage Breakdown</h3>
        <table className="rep-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Stage</th>
              <th style={{ textAlign: 'right' }}>Count</th>
              <th style={{ textAlign: 'right' }}>Value</th>
              <th style={{ textAlign: 'right' }}>% of Total</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((s: Stage, i: number) => (
              <tr key={i}>
                <td style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: CHART_PALETTE[i % CHART_PALETTE.length],
                    }}
                  />
                  {s.stage}
                </td>
                <td style={{ textAlign: 'right' }}>{s.count}</td>
                <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmt$(s.value)}</td>
                <td style={{ textAlign: 'right', color: 'var(--s600)' }}>
                  {totalPipelineValue > 0 ? ((s.value / totalPipelineValue) * 100).toFixed(1) : '0'}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TreemapChart({ stages, total }: { stages: Stage[]; total: number }) {
  const safeTotal = total || 1

  return (
    <div className="rep-treemap">
      {stages.map((stage, idx) => {
        const pct = (stage.value / safeTotal) * 100
        const color = CHART_PALETTE[idx % CHART_PALETTE.length]
        const minWidthPx = pct > 8 ? undefined : '80px'

        return (
          <div
            key={idx}
            className="rep-treemap-block"
            style={{
              flex: `${pct} 0 0%`,
              background: color,
              minWidth: minWidthPx,
            }}
          >
            <span className="rep-treemap-label">{stage.stage}</span>
            <span className="rep-treemap-value">{fmt$(stage.value)}</span>
            <span className="rep-treemap-count">{stage.count} item{stage.count !== 1 ? 's' : ''}</span>
          </div>
        )
      })}
    </div>
  )
}
